import { ApolloProvider } from "@apollo/client";
import { client } from "./lib/apollo";
import "./global.css";
import { Route, Routes } from "react-router";
import SignUpPage from "./pages/signup/index.tsx";
import SignInPage from "./pages/signin/index.tsx";
import { Toaster } from "react-hot-toast";
import SharingInvitation from "./pages/sharing-invitation/index.tsx";
import CreateOrganization from "./pages/create-organization/index.tsx";
import JoinOrganization from "./pages/join-organization/index.tsx";
import SignOutPage from "./pages/signout/index.tsx";
import AskResetPassword from "./pages/ask-reset-password/index.tsx";
import ResetPassword from "./pages/reset-password/index.tsx";
import Home from "./pages/home/index.tsx";
import DownloadApp from "./pages/download-app/index.tsx";
import { FEATURES } from "./hooks/usePermissions/permissions.ts";
import ProtectedRoute from "./components/ProtectedRoute/index.tsx";
import { ConnectedLayout } from "./components/ConnectedLayout";
import Account from "./pages/account";
import Documents from "./pages/documents";
import Collaborators from "./pages/collaborators";
import Collaborator from "./pages/collaborators/pages/collaborator";
import Events from "./pages/events";
import EventPage from "./pages/events/pages/event";
import ProjectPage from "./pages/projects/pages/project";
import Projects from "./pages/projects";
import FeatureProtectedRoute from "./components/FeatureProtectedRoute/index.tsx";
import HomeOrRedirect from "./components/HomeOrRedirect/index.tsx";
import { motion } from "motion/react";
import SwitchOrganization from "./pages/switch-organization/index.tsx";
import { NuqsAdapter } from "nuqs/adapters/react";
import Dashboard from "./pages/dashboard/index.tsx";

type RouteType = {
  path: string;
  element: React.ReactNode;
  feature: FEATURES | null;
};

const publicRoutes: RouteType[] = [
  {
    path: "/",
    element: <Home />,
    feature: null,
  },
  {
    path: "/signin",
    element: <SignInPage />,
    feature: null,
  },
  {
    path: "/signup",
    element: <SignUpPage />,
    feature: null,
  },
  {
    path: "/signout",
    element: <SignOutPage />,
    feature: null,
  },
  {
    path: "/sharing-invitation/:invitationCode",
    element: <SharingInvitation />,
    feature: null,
  },
  {
    path: "/create-organization",
    element: <CreateOrganization />,
    feature: null,
  },
  {
    path: "/join-organization",
    element: <JoinOrganization />,
    feature: null,
  },
  {
    path: "/ask-reset-password",
    element: <AskResetPassword />,
    feature: null,
  },
  {
    path: "/reset-password/:passwordResetId",
    element: <ResetPassword />,
    feature: null,
  },
  {
    path: "/download-app",
    element: <DownloadApp />,
    feature: null,
  },
];

const privateRoutes: RouteType[] = [
  {
    path: "/dashboard",
    element: <Dashboard />,
    feature: null,
  },
  {
    path: "/account",
    element: <Account />,
    feature: null,
  },
  {
    path: "/documents",
    element: <Documents />,
    feature: null,
  },
  {
    path: "/collaborators",
    element: <Collaborators />,
    feature: null,
  },
  {
    path: "/collaborators/:userOrganizationId",
    element: <Collaborator />,
    feature: null,
  },
  {
    path: "/events",
    element: <Events />,
    feature: null,
  },
  {
    path: "/events/:id",
    element: <EventPage />,
    feature: null,
  },
  {
    path: "/projects",
    element: <Projects />,
    feature: null,
  },
  {
    path: "/projects/:id",
    element: <ProjectPage />,
    feature: null,
  },
  {
    path: "/switch-organization",
    element: <SwitchOrganization />,
    feature: null,
  },
];

function App() {
  return (
    <ApolloProvider client={client}>
      <NuqsAdapter>
        <Routes>
          {/* Home route - shows Home if not authenticated, redirects to dashboard if authenticated */}
          <Route path="/" element={<HomeOrRedirect />} />

          {/* Public routes */}
          {publicRoutes
            .filter((route) => route.path !== "/") // Exclude "/" as it's handled above
            .map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            ))}

          {/* Private routes */}
          {privateRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                <ProtectedRoute>
                  <FeatureProtectedRoute feature={route.feature}>
                    <ConnectedLayout>
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        {route.element}
                      </motion.div>
                    </ConnectedLayout>
                  </FeatureProtectedRoute>
                </ProtectedRoute>
              }
            />
          ))}
        </Routes>

        <Toaster
          position="top-right"
          toastOptions={{
            className: "",
            style: {
              background: "#242731",
              color: "white",
            },
          }}
        />
      </NuqsAdapter>
    </ApolloProvider>
  );
}

export default App;
